import React from 'react';
import { ListChecks, MessageSquarePlus, Settings2 } from 'lucide-react';

import type { User as AuthUser } from '@/features/auth/AuthContext';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import type {
  FeedbackModeController,
  FeedbackPermissions,
  FeedbackThemeTokens,
  FeedbackUser,
} from '@dope-ai/feedback-mode';

import {
  createManagementToolFeedbackBackend,
  getFeedbackProvider,
  isFeedbackModeAuthReady,
  listFeedbackComments,
  loadFeedbackModePackage,
  type FeedbackCapabilities,
  type FeedbackProviderState,
} from './feedbackModeClient';

type FeedbackModeHostProps = {
  projectId?: string | null;
  pageKey: string;
  routeLabel: string;
  user?: AuthUser | null;
  canManageSettings?: boolean;
  onOpenSettings?: () => void;
};

const capabilityPermissionNames = (capabilities: FeedbackCapabilities): string[] =>
  Object.entries(capabilities)
    .filter(([, enabled]) => enabled)
    .map(([key]) => `feedback.${key}`);

const initializationRetryDelays = [500, 1000, 2000];

const FEEDBACK_CAPABILITY_KEYS: Array<keyof FeedbackCapabilities> = ['read', 'create', 'updateOwn', 'resolve', 'manage', 'export'];

// Whether a freshly resolved provider would change what the overlay may show or do. Rebuilding only
// on a real change keeps an open annotation alive across unrelated session reloads.
const feedbackAuthorityChanged = (current: FeedbackProviderState | null, next: FeedbackProviderState): boolean => {
  if (!current) return next.enabled;
  if (current.enabled !== next.enabled || current.orgId !== next.orgId || current.projectId !== next.projectId) return true;
  if (current.userId !== next.userId || (current.membershipRole ?? null) !== (next.membershipRole ?? null)) return true;
  return FEEDBACK_CAPABILITY_KEYS.some((key) => Boolean(current.capabilities[key]) !== Boolean(next.capabilities[key]));
};

// The overlay only needs these fields; depending on them (not the user object) keeps a session
// reload from tearing the overlay down while someone is annotating.
type FeedbackUserIdentity = { id: string; username: string; email: string; role: string };

const buildFeedbackUser = (
  provider: FeedbackProviderState,
  user: FeedbackUserIdentity | null,
): FeedbackUser => {
  const roles = [
    user?.role,
    provider.membershipRole || undefined,
  ].filter((role): role is string => Boolean(role));

  return {
    id: provider.userId,
    name: user?.username || user?.email || provider.userId,
    email: user?.email || undefined,
    orgId: provider.orgId,
    projectId: provider.projectId,
    roles,
    permissions: capabilityPermissionNames(provider.capabilities),
    source: 'authenticated',
  };
};

const feedbackPermissions = (capabilities: FeedbackCapabilities): FeedbackPermissions => ({
  read: capabilities.read,
  create: capabilities.create,
  updateOwn: capabilities.updateOwn,
  resolve: capabilities.resolve,
  manage: capabilities.manage,
  export: capabilities.export,
});

const readDocumentDarkMode = (): boolean => {
  if (typeof document === 'undefined') return true;
  return document.documentElement.classList.contains('dark');
};

const buildManagementToolFeedbackTheme = (isDarkMode: boolean): FeedbackThemeTokens => {
  if (isDarkMode) {
    return {
      primary: '#3b82f6',
      primaryForeground: '#f8fafc',
      background: '#171717',
      panel: 'rgba(32, 32, 32, 0.98)',
      surface: '#262626',
      text: '#f5f7fb',
      muted: '#9ca3af',
      border: '#333333',
      ring: '#60a5fa',
      success: '#34d399',
      warning: '#fbbf24',
      danger: '#f87171',
      radius: '10px',
    };
  }

  return {
    primary: '#2563eb',
    primaryForeground: '#ffffff',
    background: '#f4f4f5',
    panel: 'rgba(255, 255, 255, 0.98)',
    surface: '#f8fafc',
    text: '#18181b',
    muted: '#71717a',
    border: '#e4e4e7',
    ring: '#93c5fd',
    success: '#059669',
    warning: '#d97706',
    danger: '#dc2626',
    radius: '10px',
  };
};

// The package applies its theme as CSS custom properties on the <feedback-mode> host
// (see @dope-ai/feedback-mode applyHostTheme). Restyling the live host keeps an open
// annotation instead of destroying and recreating the overlay on every dark-mode toggle.
const FEEDBACK_THEME_CSS_VARIABLES: Array<[keyof FeedbackThemeTokens, string]> = [
  ['primary', '--fm-primary'],
  ['primaryForeground', '--fm-primary-foreground'],
  ['background', '--fm-background'],
  ['panel', '--fm-panel'],
  ['surface', '--fm-surface'],
  ['text', '--fm-text'],
  ['muted', '--fm-muted'],
  ['border', '--fm-border'],
  ['ring', '--fm-ring'],
  ['success', '--fm-success'],
  ['warning', '--fm-warning'],
  ['danger', '--fm-danger'],
  ['radius', '--fm-radius'],
];

const applyFeedbackHostTheme = (theme: FeedbackThemeTokens): void => {
  if (typeof document === 'undefined') return;
  document.querySelectorAll<HTMLElement>('feedback-mode').forEach((host) => {
    FEEDBACK_THEME_CSS_VARIABLES.forEach(([key, variable]) => {
      const value = theme[key];
      if (typeof value === 'string' && value.trim()) host.style.setProperty(variable, value.trim());
    });
  });
};

export const FeedbackModeHost: React.FC<FeedbackModeHostProps> = ({
  projectId,
  pageKey,
  routeLabel,
  user,
  canManageSettings = false,
  onOpenSettings,
}) => {
  const { language, t } = useSettings();
  const [provider, setProvider] = React.useState<FeedbackProviderState | null>(null);
  const [commentCount, setCommentCount] = React.useState(0);
  const [controllerReady, setControllerReady] = React.useState(false);
  const [isAnnotating, setIsAnnotating] = React.useState(false);
  const [isDarkMode, setIsDarkMode] = React.useState(readDocumentDarkMode);
  const [authorityRevision, setAuthorityRevision] = React.useState(0);
  const controllerRef = React.useRef<FeedbackModeController | null>(null);
  const providerRef = React.useRef<FeedbackProviderState | null>(null);
  providerRef.current = provider;
  const feedbackTheme = React.useMemo(
    () => buildManagementToolFeedbackTheme(isDarkMode),
    [isDarkMode],
  );
  const feedbackThemeRef = React.useRef(feedbackTheme);
  feedbackThemeRef.current = feedbackTheme;
  const routeLabelRef = React.useRef(routeLabel);
  routeLabelRef.current = routeLabel;
  const userId = user?.id ?? null;
  const userName = user?.username ?? null;
  const userEmail = user?.email ?? null;
  const userRole = user?.role ?? null;
  const feedbackUserIdentity = React.useMemo<FeedbackUserIdentity | null>(
    () => (userId === null ? null : { id: userId, username: userName ?? '', email: userEmail ?? '', role: userRole ?? '' }),
    [userEmail, userId, userName, userRole],
  );

  const isPackageCaptureActive = React.useCallback(() => {
    const root = document.querySelector('feedback-mode')?.shadowRoot?.querySelector('.fm-root');
    const active = root?.getAttribute('data-active');
    return active == null ? null : active === 'true';
  }, []);

  React.useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
      return;
    }
    const syncTheme = () => setIsDarkMode(readDocumentDarkMode());
    const observer = new MutationObserver(syncTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    syncTheme();
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    let cancelled = false;
    let checkRequest = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const refreshAuthority = () => setAuthorityRevision((revision) => revision + 1);
    // An admin's capability or access change reaches this user as the socket's auth-updated (or
    // project_access_updated) event. Re-resolve the provider and rebuild the overlay only if feedback
    // authority really changed; while the session is re-authenticating, retry with the init delays.
    const checkFeedbackAuthority = (attempt = 0) => {
      if (!projectId) return;
      const request = ++checkRequest;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      retryTimer = undefined;
      void getFeedbackProvider(projectId, pageKey).then((next) => {
        if (cancelled || request !== checkRequest) return;
        if (next.reasons.includes('auth_not_ready') && attempt < initializationRetryDelays.length) {
          retryTimer = setTimeout(() => checkFeedbackAuthority(attempt + 1), initializationRetryDelays[attempt]);
          return;
        }
        if (feedbackAuthorityChanged(providerRef.current, next)) refreshAuthority();
      }).catch(() => undefined);
    };
    const handleSessionAuthorityUpdate = () => checkFeedbackAuthority();
    window.addEventListener('feedback-mode:authority-changed', refreshAuthority);
    window.addEventListener('auth-updated', handleSessionAuthorityUpdate);
    window.addEventListener('project_access_updated', handleSessionAuthorityUpdate);
    return () => {
      cancelled = true;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      window.removeEventListener('feedback-mode:authority-changed', refreshAuthority);
      window.removeEventListener('auth-updated', handleSessionAuthorityUpdate);
      window.removeEventListener('project_access_updated', handleSessionAuthorityUpdate);
    };
  }, [pageKey, projectId]);

  React.useEffect(() => {
    if (controllerReady) applyFeedbackHostTheme(feedbackTheme);
  }, [controllerReady, feedbackTheme]);

  React.useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let retryCount = 0;
    let commentRequest = 0;
    let ownedController: FeedbackModeController | null = null;
    controllerRef.current?.destroy();
    controllerRef.current = null;
    setControllerReady(false);
    setIsAnnotating(false);
    setProvider(null);
    setCommentCount(0);

    if (!projectId) {
      return () => {
        cancelled = true;
      };
    }

    const handleInitializationFailure = () => {
      if (cancelled) return;
      ownedController?.destroy();
      if (controllerRef.current === ownedController) controllerRef.current = null;
      ownedController = null;
      setProvider(null);
      setControllerReady(false);
      setIsAnnotating(false);
    };

    const scheduleRetry = () => {
      if (cancelled || retryCount >= initializationRetryDelays.length) return;
      retryTimer = setTimeout(() => {
        retryTimer = undefined;
        if (!cancelled) void initialize().catch(handleInitializationFailure);
      }, initializationRetryDelays[retryCount++]);
    };

    const initialize = async () => {
      const authReady = await isFeedbackModeAuthReady();
      if (cancelled) return;
      if (!authReady) {
        scheduleRetry();
        return;
      }

      const nextProvider = await getFeedbackProvider(projectId, pageKey);

      if (cancelled) return;
      setProvider(nextProvider);
      if (!nextProvider.enabled || (!nextProvider.capabilities.create && !nextProvider.capabilities.read)) {
        if (nextProvider.reasons.includes('auth_not_ready')) scheduleRetry();
        return;
      }

      const feedbackPackage = await loadFeedbackModePackage();
      if (cancelled) return;
      if (!feedbackPackage) {
        scheduleRetry();
        return;
      }

      const refreshCommentCount = async () => {
        if (cancelled || !nextProvider.capabilities.read) return;
        const request = ++commentRequest;
        try {
          const comments = await listFeedbackComments(nextProvider.orgId, nextProvider.projectId, pageKey);
          if (!cancelled && request === commentRequest) setCommentCount(comments.length);
        } catch {
          return;
        }
      };

      const controller = feedbackPackage.initFeedbackMode({
        mode: 'production',
        controls: { mode: 'external' },
        theme: feedbackThemeRef.current,
        provider: {
          org: {
            id: nextProvider.orgId,
            name: nextProvider.orgName,
          },
          project: {
            id: nextProvider.projectId,
            name: routeLabelRef.current,
            environment: import.meta.env.MODE,
          },
          user: buildFeedbackUser(nextProvider, feedbackUserIdentity),
          permissions: feedbackPermissions(nextProvider.capabilities),
          backend: createManagementToolFeedbackBackend(() => { void refreshCommentCount(); }),
          theme: feedbackThemeRef.current,
          locale: language,
          resolvePage: () => ({
            pageKey,
            route: routeLabelRef.current,
            url: window.location.href,
            title: document.title || routeLabelRef.current,
          }),
        },
      });

      if (cancelled) {
        controller.destroy();
        return;
      }

      ownedController = controller;
      controllerRef.current = controller;
      setControllerReady(true);
      await refreshCommentCount();
    };

    void initialize().catch(handleInitializationFailure);

    return () => {
      cancelled = true;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      ownedController?.destroy();
      if (controllerRef.current === ownedController) controllerRef.current = null;
      setIsAnnotating(false);
    };
  }, [authorityRevision, feedbackUserIdentity, language, pageKey, projectId]);

  React.useEffect(() => {
    if (!controllerReady || typeof window === 'undefined') {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      const packageActive = isPackageCaptureActive();
      if (!isAnnotating && packageActive !== true) {
        return;
      }

      controllerRef.current?.setActive(false);
      setIsAnnotating(false);
    };

    window.addEventListener('keydown', handleEscape, true);
    return () => window.removeEventListener('keydown', handleEscape, true);
  }, [controllerReady, isAnnotating, isPackageCaptureActive]);

  const settingsLauncher = canManageSettings && onOpenSettings ? (
    <button
      type="button"
      onClick={onOpenSettings}
      className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-[11px] font-medium text-gray-600 transition-colors hover:bg-zinc-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
      aria-label={t('Open Feedback Mode settings')}
      title={t('Open Feedback Mode settings')}
      data-testid="feedback-mode-settings-trigger"
    >
      <Settings2 className="h-3.5 w-3.5" aria-hidden="true" />
      <span>{t('Feedback settings')}</span>
    </button>
  ) : null;

  if (!projectId) {
    return settingsLauncher;
  }

  const providerDisablesFeedback = Boolean(
    provider && (!provider.enabled || (!provider.capabilities.create && !provider.capabilities.read)),
  );

  if (providerDisablesFeedback) {
    return settingsLauncher;
  }

  if (!provider) {
    return settingsLauncher;
  }

  if (!controllerReady) {
    return settingsLauncher;
  }

  const toggleAnnotating = () => {
    const packageActive = isPackageCaptureActive();
    const nextActive = !(packageActive ?? isAnnotating);
    if (nextActive) {
      controllerRef.current?.setReviewOpen(false);
    }
    controllerRef.current?.setActive(nextActive);
    setIsAnnotating(nextActive);
  };

  const openReviewList = () => {
    controllerRef.current?.setActive(false);
    setIsAnnotating(false);
    controllerRef.current?.setReviewOpen(true);
  };

  const iconButtonBase =
    'relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-transparent transition-colors';
  const iconButtonIdle =
    'text-gray-700 hover:bg-zinc-100 dark:text-gray-300 dark:hover:bg-gray-800';

  return (
    <div className="relative flex items-center gap-1 font-sans" style={{ zIndex: 2147483020 }}>
      {settingsLauncher}
      {provider.capabilities.create ? (
        <button
          type="button"
          onClick={toggleAnnotating}
          className={`${iconButtonBase} ${
            isAnnotating
              ? 'bg-sky-100 text-sky-700 dark:bg-sky-950/80 dark:text-sky-200'
              : iconButtonIdle
          }`}
          aria-label={t('Annotate feedback')}
          aria-pressed={isAnnotating}
          title={t('Annotate feedback')}
          data-feedback-mode-external-control="true"
          data-testid="feedback-annotator-trigger"
        >
          <MessageSquarePlus className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}

      {provider.capabilities.read ? (
        <button
          type="button"
          onClick={openReviewList}
          className={`${iconButtonBase} ${iconButtonIdle}`}
          aria-label={t('Feedback list')}
          title={t('Feedback list')}
          data-feedback-mode-external-control="true"
          data-testid="feedback-list-trigger"
        >
          <ListChecks className="h-4 w-4" aria-hidden="true" />
          {commentCount ? (
            <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-sky-500" aria-hidden="true" />
          ) : null}
        </button>
      ) : null}
    </div>
  );
};
